import { MailSequenceReferenceDateType } from '../../../generated/graphql';
import { SavedActivityInputPreview } from '../../lib/followUp';
import {
  TemplateStepDraft,
  StepActivitiesState,
  StepActivitySelection,
  StepResourceModeState,
  migrateStepActivitySelection,
} from '../../lib/mailSequence';

const STORAGE_KEY = 'homecoming.schedule-mail.draft';
const DRAFT_VERSION = 4;

type LegacyScheduleMailDraft = {
  patientsOrTags: unknown[];
  activities: SavedActivityInputPreview[];
  referenceDateType: MailSequenceReferenceDateType;
  defaultSendTime: string;
  templateId: string;
  sequenceName?: string;
  steps: TemplateStepDraft[];
  customAnchorDate: string;
  stepActivitySelection?: StepActivitySelection;
  stepActivitiesState?: StepActivitiesState;
  stepResourceModes?: StepResourceModeState;
  stepSourceTemplateIds?: Record<number, string>;
  stepSourceTemplateNames?: Record<number, string>;
};

export type ScheduleMailDraft = {
  version?: number;
  patientsOrTags: unknown[];
  activities: SavedActivityInputPreview[];
  referenceDateType: MailSequenceReferenceDateType;
  defaultSendTime: string;
  templateId: string;
  sequenceName: string;
  steps: TemplateStepDraft[];
  customAnchorDate: string;
  stepActivitiesState: StepActivitiesState;
  stepResourceModes: StepResourceModeState;
  stepSourceTemplateIds: Record<number, string>;
  stepSourceTemplateNames: Record<number, string>;
};

export const hasComposerPrefill = (
  locationState: {
    recipientPatients?: unknown[];
    activities?: unknown[];
    subject?: string;
    providerNote?: string;
  } | null,
): boolean =>
  Boolean(
    locationState?.recipientPatients?.length ||
      locationState?.activities?.length ||
      locationState?.subject ||
      locationState?.providerNote,
  );

export const inferStepResourceModesFromLegacy = (
  stepActivitiesState: StepActivitiesState,
): StepResourceModeState => {
  const modes: StepResourceModeState = {};
  Object.keys(stepActivitiesState).forEach((key) => {
    modes[Number(key)] = 'custom';
  });
  return modes;
};

const normalizeDraft = (
  parsed: LegacyScheduleMailDraft,
): ScheduleMailDraft | null => {
  if (!parsed || typeof parsed !== 'object') return null;
  if (!Array.isArray(parsed.patientsOrTags)) return null;
  if (!Array.isArray(parsed.activities)) return null;
  if (!Array.isArray(parsed.steps)) return null;

  let stepActivitiesState = parsed.stepActivitiesState ?? {};
  if (
    !parsed.stepActivitiesState &&
    parsed.stepActivitySelection &&
    Object.keys(parsed.stepActivitySelection).length > 0
  ) {
    stepActivitiesState = migrateStepActivitySelection(
      parsed.stepActivitySelection,
      parsed.activities,
    );
  }

  return {
    version: DRAFT_VERSION,
    patientsOrTags: parsed.patientsOrTags,
    activities: parsed.activities,
    referenceDateType: parsed.referenceDateType,
    defaultSendTime: parsed.defaultSendTime,
    templateId: parsed.templateId ?? '',
    sequenceName: parsed.sequenceName ?? '',
    steps: parsed.steps,
    customAnchorDate: parsed.customAnchorDate ?? '',
    stepActivitiesState,
    stepResourceModes:
      parsed.stepResourceModes ??
      inferStepResourceModesFromLegacy(stepActivitiesState),
    stepSourceTemplateIds: parsed.stepSourceTemplateIds ?? {},
    stepSourceTemplateNames: parsed.stepSourceTemplateNames ?? {},
  };
};

export const loadScheduleMailDraft = (): ScheduleMailDraft | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizeDraft(JSON.parse(raw) as LegacyScheduleMailDraft);
  } catch {
    return null;
  }
};

export const saveScheduleMailDraft = (draft: ScheduleMailDraft): void => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...draft, version: DRAFT_VERSION }),
    );
  } catch {
    // sessionStorage full or unavailable
  }
};

export const clearScheduleMailDraft = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
